import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '../../../componentsv2/Typography';
import Form from '../../../componentsv2/Form';
import AutocompleteField from '../../../componentsv2/Field/AutocompleteField';
import CountryFlag from '../../../componentsv2/CountryFlag';
import StepButtons from './StepButtons';
import { useTranslation } from '../../../hooks/i18n';
import { getCountriesForActivation } from '../../../constants/data';
import { CountryOption, CountryKeys } from '../../../types';
import { COLORS } from '../../../themev2';

interface SelectCountryProps {
  initialCountry?: CountryKeys;
  onSubmit: (country: CountryKeys) => void;
  isLoading?: boolean;
}

interface FormValues {
  country: CountryKeys;
}

const SelectCountry = ({ initialCountry, onSubmit, isLoading }: SelectCountryProps) => {
  const { t } = useTranslation(['activation', 'common']);

  const validationSchema = Yup.object({
    country: Yup.string().required(t('common:requiredField')),
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Formik
        initialValues={{
          country: initialCountry || ('' as CountryKeys),
        }}
        validationSchema={validationSchema}
        onSubmit={(values: FormValues) => {
          onSubmit(values.country);
        }}
        enableReinitialize
      >
        {({ values, submitForm }: FormikProps<FormValues>) => (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Grid container rowSpacing={3}>
              <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Typography
                  sx={{ fontWeight: 600, fontSize: '24px', lineHeight: '36px', color: COLORS.brand.night }}
                >
                  {t('activation:step1.title')}
                </Typography>
                <Typography
                  sx={{ fontSize: '18px', fontWeight: 400, lineHeight: '27px', color: COLORS.gray[1100] }}
                >
                  {t('activation:step1.subtitle')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Form name="select-country-form" id="select-country-form">
                  <Grid container rowSpacing={2}>
                    <Grid item xs={12} md={6}>
                      <AutocompleteField
                        placeholder={t('activation:step1.countryLabel')}
                        label={t('activation:step1.countryLabel')}
                        name="country"
                        options={getCountriesForActivation(t)
                          .sort((a: CountryOption, b: CountryOption) => a.country.localeCompare(b.country))
                          .map(({ country, countryKey }) => ({
                            value: countryKey,
                            label: country,
                          }))}
                        renderOption={({ label, value }) => (
                          <div style={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                            <div
                              style={{
                                height: '24px',
                                width: '24px',
                                borderRadius: '20%',
                                marginRight: '1em',
                                overflow: 'hidden',
                                flexShrink: 0,
                                flexGrow: 0,
                              }}
                            >
                              <CountryFlag countryCode={value} size={24} />
                            </div>
                            <div
                              style={{
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                minWidth: 0,
                                flexGrow: 1,
                              }}
                            >
                              {label}
                            </div>
                          </div>
                        )}
                        inputStartAdornment={
                          values.country ? (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginRight: '0.2em',
                              }}
                            >
                              <CountryFlag countryCode={values.country} size={24} />
                            </div>
                          ) : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                </Form>
              </Grid>
            </Grid>
            <StepButtons onNext={submitForm} isNextLoading={isLoading} />
          </Box>
        )}
      </Formik>
    </Box>
  );
};

export default SelectCountry;
